import Sval from 'sval';
import { parse } from 'acorn';

/** Max loop iterations / function calls allowed per test call. */
const MAX_TICKS = 2_000_000;
/** Max wall-clock time (ms) allowed per test call. */
const MAX_TIME_MS = 1500;

export type CallResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string };

export type CompileResult =
  | { ok: true; call: (args: unknown[]) => CallResult }
  | { ok: false; error: string };

type AstNode = { type: string; [key: string]: unknown };

const LOOP_TYPES = new Set([
  'ForStatement',
  'ForInStatement',
  'ForOfStatement',
  'WhileStatement',
  'DoWhileStatement',
]);

const FUNCTION_TYPES = new Set([
  'FunctionDeclaration',
  'FunctionExpression',
  'ArrowFunctionExpression',
]);

function tickStatement(): AstNode {
  return {
    type: 'ExpressionStatement',
    expression: {
      type: 'CallExpression',
      callee: { type: 'Identifier', name: '__tick' },
      arguments: [],
      optional: false,
    },
  };
}

function prependTick(body: AstNode): AstNode {
  if (body.type === 'BlockStatement') {
    return { ...body, body: [tickStatement(), ...(body.body as AstNode[])] };
  }
  return { type: 'BlockStatement', body: [tickStatement(), body] };
}

/** Inject a `__tick()` call at the start of every loop body and function body. */
function instrument(node: unknown): void {
  if (Array.isArray(node)) {
    node.forEach(instrument);
    return;
  }
  if (!node || typeof node !== 'object') return;
  const n = node as AstNode;
  if (typeof n.type !== 'string') return;

  for (const key of Object.keys(n)) {
    if (key === 'loc' || key === 'range') continue;
    instrument(n[key]);
  }

  if (LOOP_TYPES.has(n.type)) {
    n.body = prependTick(n.body as AstNode);
  } else if (FUNCTION_TYPES.has(n.type)) {
    const body = n.body as AstNode;
    if (body.type === 'BlockStatement') {
      n.body = prependTick(body);
    } else {
      // Arrow function with an expression body: `x => expr`
      n.body = {
        type: 'BlockStatement',
        body: [tickStatement(), { type: 'ReturnStatement', argument: body }],
      };
      n.expression = false;
    }
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) {
    const name = e.name && e.name !== 'Error' ? `${e.name}: ` : '';
    return `${name}${e.message}`;
  }
  return String(e);
}

class TimeoutError extends Error {
  constructor() {
    super('Timed out. Your code ran too long. Do you have an infinite loop?');
    this.name = 'Timeout';
  }
}

function cloneArgs(args: unknown[]): unknown[] {
  try {
    return structuredClone(args);
  } catch {
    return [...args];
  }
}

/**
 * Compile user source code and extract the function named `fnName`.
 * Code runs inside the Sval sandboxed interpreter (no `eval` needed),
 * with loop/recursion guards so infinite loops can't freeze the page.
 */
export function compile(code: string, fnName: string): CompileResult {
  let ticks = 0;
  let deadline = 0;

  const reset = () => {
    ticks = 0;
    deadline = Date.now() + MAX_TIME_MS;
  };

  const tick = () => {
    ticks++;
    if (ticks > MAX_TICKS || (ticks % 512 === 0 && Date.now() > deadline)) {
      throw new TimeoutError();
    }
  };

  const source = `${code}\n;exports.__fn = (typeof ${fnName} === 'function') ? ${fnName} : undefined;`;

  let ast: AstNode;
  try {
    ast = parse(source, { ecmaVersion: 'latest', sourceType: 'script' }) as unknown as AstNode;
  } catch (e) {
    return { ok: false, error: `Syntax error: ${e instanceof Error ? e.message : String(e)}` };
  }

  instrument(ast);

  const interpreter = new Sval({ ecmaVer: 'latest', sandBox: true });
  interpreter.import({ __tick: tick });

  reset();
  try {
    // Sval accepts an ESTree-compatible AST
    interpreter.run(ast as unknown as Parameters<typeof interpreter.run>[0]);
  } catch (e) {
    return { ok: false, error: errorMessage(e) };
  }

  const fn = (interpreter.exports as Record<string, unknown>).__fn;
  if (typeof fn !== 'function') {
    return { ok: false, error: `Could not find a function named "${fnName}". Keep the function name the same as the starter code.` };
  }

  return {
    ok: true,
    call: (args: unknown[]) => {
      reset();
      try {
        const value = (fn as (...a: unknown[]) => unknown)(...cloneArgs(args));
        return { ok: true, value };
      } catch (e) {
        return { ok: false, error: errorMessage(e) };
      }
    },
  };
}

/** Structural equality for primitives, arrays, and plain objects. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/** Format a JS value the way you'd write it in source code. */
export function formatValue(v: unknown): string {
  if (v === undefined) return 'undefined';
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number') return Object.is(v, -0) ? '-0' : String(v);
  if (typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  if (typeof v === 'function') return 'function';
  if (Array.isArray(v)) return `[${v.map(formatValue).join(', ')}]`;
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return `{ ${entries.map(([k, val]) => `${/^[A-Za-z_$][\w$]*$/.test(k) ? k : JSON.stringify(k)}: ${formatValue(val)}`).join(', ')} }`;
  }
  return String(v);
}

export function formatCall(fnName: string, args: unknown[]): string {
  return `${fnName}(${args.map(formatValue).join(', ')})`;
}
